import React, { useEffect, useState } from "react";
import { StyledBodyProfessionalPathDevelopment } from "./ProfessionalPathDevelopment.styled";
import {
  ACTION_PLAN_LABEL,
  EMPTY_PROFESSIONAL_PATH_MESSAGE,
  FILL_IN_LABEL,
  FOCUS_GOAL_LABEL,
} from "../../../../../constants/Dictionary";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormField } from "../../../../../components/core/FormField/FormField";
import TextArea from "../../../../../components/core/TextArea/TextArea";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { UpdateCollaboratorCmSheetCareerPathRequest } from "../../../../../types/api/collaborators.type";
import { TextBox } from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import professionalDevelopment from "../../../../../images/professionalDevelopment.png";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { ReactComponent as FillInIcon } from "../../../../../icons/fillin.svg";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import { CmSheetSectionProps } from "../SchedaCM";
import {
  CmSheetStateEnum,
  CHARACTER_MAX_LENGTH,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import {
  useCmSheetCareerPath,
  useUpdateCmSheetCareerPath,
} from "../../../../../hooks/cmSheet/careerPath.hook";

type FormData = {
  developmentGoalFocus?: string;
  actionPlan?: string;
};

export const ProfessionalPathDevelopment = ({
  footer,
  cmSheetInfo,
  cmSheetStateMachineInfo,
  showFooter,
  isMyProfile,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const { data, isFetching } = useCmSheetCareerPath(cmSheetInfo.id);

  const { updateCareerPath, isUpdating: updating } = useUpdateCmSheetCareerPath(
    cmSheetInfo.id,
  );

  const schema = yup
    .object({
      developmentGoalFocus: yup.string(),
      actionPlan: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const body: UpdateCollaboratorCmSheetCareerPathRequest = {
        developmentGoalFocus: data.developmentGoalFocus ?? "",
        actionPlan: data.actionPlan ?? "",
      };

      await updateCareerPath(body);
    } finally {
      setHasData(null);
      setEdit(false);
    }
  };

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      reset({
        developmentGoalFocus: data?.developmentGoalFocus ?? "",
        actionPlan: data?.actionPlan ?? "",
      });

      if (data && hasData === null) {
        const newHasData = !!(data.developmentGoalFocus || data.actionPlan);
        setHasData(newHasData);
      }
    }
  }, [data, hasData, edit, isFetching]);

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData && !edit;
  const showPage = hasData === true || edit;

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_PROFESSIONAL_PATH_CM_SHEET,
        ) &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="professionalPathDevelopment"
        disabledEditButton={!isDirty || updating}
        edit={edit}
        showEdit={canEdit() && (!!hasData || edit)}
        setEdit={setEdit}
        showFooter={showFooter}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    updating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={canEdit() ? EMPTY_PROFESSIONAL_PATH_MESSAGE : ""}
            imageSrc={professionalDevelopment}
          >
            {canEdit() && (
              <Button
                label={FILL_IN_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<FillInIcon />}
                onClick={() => (setEdit ? setEdit(true) : {})}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={updating}>
          <StyledHideOrShowContainer $show={edit}>
            <StyledBodyProfessionalPathDevelopment>
              <form
                id="professionalPathDevelopment"
                onSubmit={handleSubmit(onSubmit)}
              >
                <FormField error={errors.developmentGoalFocus?.message}>
                  <Controller
                    name="developmentGoalFocus"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label={FOCUS_GOAL_LABEL}
                        maxLength={CHARACTER_MAX_LENGTH}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.actionPlan?.message}>
                  <Controller
                    name="actionPlan"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label={ACTION_PLAN_LABEL}
                        maxLength={CHARACTER_MAX_LENGTH}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </form>
            </StyledBodyProfessionalPathDevelopment>
          </StyledHideOrShowContainer>
          <StyledHideOrShowContainer $show={!edit}>
            <StyledBodyProfessionalPathDevelopment>
              <TextBox
                label={FOCUS_GOAL_LABEL}
                text={data?.developmentGoalFocus}
              />
              <TextBox label={ACTION_PLAN_LABEL} text={data?.actionPlan} />
            </StyledBodyProfessionalPathDevelopment>
          </StyledHideOrShowContainer>
        </Loader>
      </StyledHideOrShowContainer>
    </>
  );
};
